import { avatarSrc } from "@bn-games/auth-client";
import { gameLabel, type Invite } from "@bn-games/social-client";
import { t } from "@bn-games/i18n-core";
import { el } from "../components/dom";

// The sender's displayName comes from their OAuth provider, so it is
// user-supplied text. Every string below reaches the DOM through `el`'s `text`
// prop (textContent) or an `attrs` entry (setAttribute) — never `html` — so
// nothing here needs escaping: markup in a display name renders as literal
// characters.

const AVATAR_SIZE_PX = "32";

export interface InviteCardOptions {
  readonly invite: Invite;
  /** Pending invites behind this one; `0` renders no overflow note. */
  readonly moreCount: number;
  /** True while an accept/decline is in flight — both buttons go disabled. */
  readonly busy: boolean;
  readonly error: string | null;
  readonly onAccept: () => void;
  readonly onDecline: () => void;
}

function createAvatar(invite: Invite): HTMLElement {
  const src = avatarSrc(invite.from.avatarUrl);
  return el("img", {
    class: "invite-banner__avatar",
    attrs: {
      ...(src !== undefined ? { src } : {}),
      alt: "",
      width: AVATAR_SIZE_PX,
      height: AVATAR_SIZE_PX,
    },
  });
}

function createButton(
  className: string,
  label: string,
  accessibleName: string,
  busy: boolean,
  onClick: () => void,
): HTMLButtonElement {
  const button = el("button", {
    class: className,
    text: label,
    attrs: { type: "button", "aria-label": accessibleName },
    on: { click: () => onClick() },
  });
  button.disabled = busy;
  return button;
}

/**
 * One pending invite, rendered as a row: who sent it, which game, and the two
 * answers. Accessible names on the buttons name the sender and the game, since
 * "Accept" alone tells a screen-reader user nothing about what they are
 * accepting.
 */
export function createInviteCard(opts: InviteCardOptions): HTMLElement {
  const { invite, moreCount, busy } = opts;
  const sender = invite.from.displayName;
  const game = gameLabel(invite.game);

  const text = el("div", { class: "invite-banner__text" }, [
    el("span", {
      class: "invite-banner__message",
      text: t("social.invite.message", { sender, game }),
    }),
    moreCount > 0
      ? el("span", {
          class: "invite-banner__more",
          text: t("social.invite.moreCount", { count: moreCount }),
        })
      : null,
    opts.error !== null ? el("span", { class: "invite-banner__error", text: opts.error }) : null,
  ]);

  const accept = createButton(
    "invite-banner__btn invite-banner__btn--accept",
    t("social.invite.accept"),
    t("social.invite.acceptAria", { sender, game }),
    busy,
    opts.onAccept,
  );
  const decline = createButton(
    "invite-banner__btn invite-banner__btn--decline",
    t("social.invite.decline"),
    t("social.invite.declineAria", { sender, game }),
    busy,
    opts.onDecline,
  );

  return el("div", { class: "invite-banner__card" }, [
    createAvatar(invite),
    text,
    el("div", { class: "invite-banner__actions" }, [accept, decline]),
  ]);
}
